import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import codexService from '../services/codexService';

/**
 * Custom hook for managing codex data
 * @param {string} codexId - The ID of the codex to fetch
 * @returns {object} Object containing codex data, loading state, error state, and update function
 */
export const useCodexData = (codexId) => {
  const [codex, setCodex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const fetchCodex = async () => {
      if (!codexId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const codexData = await codexService.getById(codexId);
        
        // Ensure attributeValues exists
        if (!codexData.attributeValues) {
          codexData.attributeValues = {};
        }
        
        setCodex(codexData);
      } catch (err) {
        console.error('Error fetching codex data:', err);
        setError('Failed to load codex details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchCodex();
  }, [codexId, location.search]); // Add location.search to dependencies to refetch when URL params change

  /**
   * Updates the codex data
   * @param {object} updatedData - The updated data to save
   */
  const updateCodex = async (updatedData) => {
    try {
      await codexService.update(codexId, updatedData);
      // Refresh the codex data
      const refreshedCodex = await codexService.getById(codexId);
      setCodex(refreshedCodex);
    } catch (error) {
      console.error('Error updating codex:', error);
      throw new Error('Failed to update codex');
    }
  };

  return {
    codex,
    loading,
    error,
    updateCodex,
    setCodex // Expose setCodex
  };
}; 